import React from 'react';
import { Container, Typography, Paper, Box, Grid, Button, Card, CardContent, CircularProgress, Divider } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';             // Eğitim ikonu
import MenuBookIcon from '@mui/icons-material/MenuBook';           // Katalog ikonu
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'; // Takip ikonu
import StarIcon from '@mui/icons-material/Star';                 // Yetkinlik ikonu
import { useTranslation } from 'react-i18next';

const TrainingDevelopmentPage: React.FC = () => {
  const { t } = useTranslation(['humanResources', 'common2']);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      overflowX: 'hidden'
    }}>
      <Box 
        sx={{ 
          height: 'calc(100vh - 80px)', 
          overflowY: 'auto', 
          overflowX: 'hidden',
          width: '100%',
          paddingRight: '10px',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            marginLeft: '10px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
            '&:hover': {
              background: '#555'
            }
          }
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ pb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SchoolIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                {hr('pages.training.title', 'Training and Development System')}
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
              {hr('pages.training.subtitle', 'This page is under construction. The training and development module will be available soon.')}
            </Typography>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.developmentProcess', 'Development Process')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ my: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.analysis', 'Analysis')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={100} color="success" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.completed', 'Completed')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.design', 'Design')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={15} color="primary" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.inProgress', 'In Progress')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.development', 'Development')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={0} color="warning" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.notStarted', 'Not Started')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.plannedFeatures', 'Module Features (Planned)')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <MenuBookIcon sx={{ fontSize: 40, mb: 1, color: 'info.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.training.features.catalogTitle', 'Training Catalog')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.training.features.catalogDesc', 'Listing internal and external trainings, viewing details, and categorizing them.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <AssignmentTurnedInIcon sx={{ fontSize: 40, mb: 1, color: 'success.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.training.features.assignmentTitle', 'Training Assignment and Tracking')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.training.features.assignmentDesc', 'Assigning trainings to employees or departments, and tracking participation and completion statuses.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <StarIcon sx={{ fontSize: 40, mb: 1, color: 'secondary.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.training.features.competencyTitle', 'Competency Management')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.training.features.competencyDesc', 'Tracking employee competencies and areas for improvement, and matching them with trainings.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: 'rgba(76, 175, 80, 0.04)' }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.training.whyTitle', 'Why a Training and Development System?')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body1" paragraph>
                {hr('pages.training.whyIntro', 'Continuous employee development is essential for company success. With this system:')}
              </Typography>
              
              <Box component="ul" sx={{ pl: 2 }}>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.training.benefits.competencies', 'Employee competencies improve and stay up to date.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.training.benefits.engagement', 'Employee engagement and motivation increase.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.training.benefits.career', 'Career paths become clearer and better supported.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.training.benefits.knowledgeSharing', 'Internal knowledge and expertise sharing increases.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.training.benefits.adaptation', 'Adaptation to new technologies and processes accelerates.')}</Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                {hr('pages.training.conclusion', 'Our training and development module helps you maximize employee potential by structuring learning processes, tracking competencies, and supporting career growth.')}
              </Typography>
            </Paper>

            <Box sx={{ textAlign: 'center', mt: 4, pb: 4 }}>
              <Button variant="contained" color="primary" disabled>
                {hr('pages.shared.moduleAvailableWhenReady', 'The Module Will Be Available When Ready')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TrainingDevelopmentPage; 